import React, { Suspense, lazy, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { toast } from 'sonner';
import { useLanguage } from '../../contexts/LanguageContext';
import { useIsMobile, hasWebGL } from '../../hooks/useIsMobile';
import { useInViewActive } from '../../hooks/useInViewActive';
import { JAR_TYPES, JAR_TYPE_ORDER } from '../three/jarMeta';
import { COLORS, LINE_NAMES } from '../../constants';
import { SceneBoundary } from '../SceneBoundary';
import Reveal from '../Reveal';
import KitSummary from './KitSummary';
import RecommendPicker from './RecommendPicker';
import { composeKit, kitFunctionOf } from '../../lib/kitLogic';

const KitMixerScene = lazy(() => import('../three/KitMixerScene'));
const EMPTY = Object.fromEntries(JAR_TYPE_ORDER.map((tp) => [tp, null]));

const KitMixer = ({ products = [], onAddKit, initialKit }) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  const ref = useRef();
  const summaryRef = useRef();
  const active = useInViewActive(ref);
  const [webgl] = useState(() => hasWebGL());
  const [slots, setSlots] = useState(EMPTY);
  const [selectedSlot, setSelectedSlot] = useState(JAR_TYPE_ORDER[0]);
  const [hoverProduct, setHoverProduct] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [recommendOpen, setRecommendOpen] = useState(false);

  const items = useMemo(() => JAR_TYPE_ORDER.map((tp) => slots[tp]).filter(Boolean), [slots]);
  const filled = items.length;
  const complete = filled === JAR_TYPE_ORDER.length;
  const total = items.reduce((sum, p) => sum + (p.price || 0), 0);
  const kitFunction = useMemo(() => (complete ? kitFunctionOf(items) : ''), [complete, items]);

  const assign = useCallback((product) => {
    setSlots((s) => {
      const next = { ...s, [product.product_type]: product };
      setSelectedSlot(JAR_TYPE_ORDER.find((tp) => !next[tp]) || product.product_type);
      return next;
    });
    setHoverProduct(null);
  }, []);
  const selectSlot = useCallback((type) => { setSelectedSlot(type); setHoverProduct(null); }, []);

  const randomize = () => {
    const next = {};
    JAR_TYPE_ORDER.forEach((tp) => {
      const pool = products.filter((p) => p.product_type === tp);
      next[tp] = pool[Math.floor(Math.random() * pool.length)] || null;
    });
    setSlots(next);
  };

  const reset = () => { setSlots(EMPTY); setSelectedSlot(JAR_TYPE_ORDER[0]); setHoverProduct(null); };

  const recommend = (objective) => {
    setSlots(composeKit(products, objective));
    setRecommendOpen(false);
    toast.message(`${t(`home.mix.objectives.${objective.id}`)} · ${t('home.mix.recommended')}`);
  };

  const addKit = () => onAddKit(items, { name: '', fn: kitFunction });

  useEffect(() => {
    if (complete) summaryRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [complete]);

  useEffect(() => {
    if (!initialKit?.products?.length) return;
    const next = { ...EMPTY };
    initialKit.products.forEach((p) => { if (p && JAR_TYPE_ORDER.includes(p.product_type)) next[p.product_type] = p; });
    setSlots(next);
    setSelectedSlot(JAR_TYPE_ORDER.find((tp) => !next[tp]) || JAR_TYPE_ORDER[0]);
    setTimeout(() => ref.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 400);
    toast.success(t('home.mix.loaded_shared').replace('{name}', initialKit.name || ''));
  }, [initialKit]);

  const step = t('home.mix.step').replace('{type}', t(JAR_TYPES[selectedSlot].nameKey));
  const hint = isMobile ? t('home.mix.hint_mobile') : t('home.mix.hint');
  const showTooltip = hoverProduct && !dragging;
  const topKey = dragging ? 'drop' : isMobile && showTooltip ? `tip-${hoverProduct.id}` : `step-${selectedSlot}`;

  const tooltip = (
    <motion.div key={`tip-${hoverProduct?.id}`} className={`mixer-tooltip ${isMobile ? 'is-top' : ''}`} style={{ '--line': COLORS[hoverProduct?.line_color], x: isMobile ? '-50%' : 0 }} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }} data-testid="mixer-tooltip">
      <span className="mixer-tooltip-title">{t(JAR_TYPES[hoverProduct?.product_type || selectedSlot].nameKey)}</span>
      <div className="mix-info">
        <span className="mix-info-line"><i />{LINE_NAMES[hoverProduct?.line_color]}</span>
        <strong>{hoverProduct?.main_ingredient || hoverProduct?.ingredients?.[0]}</strong>
        <span className="mix-info-effect">{hoverProduct?.effect || hoverProduct?.benefit}</span>
      </div>
    </motion.div>
  );

  return (
    <section ref={ref} id="kit-mixer" className="section mixer-section" data-testid="mixer-section">
      <div className="section-inner">
        <Reveal className="section-head">
          <span className="eyebrow">{t('home.mix.eyebrow')}</span>
          <h2 className="section-title">{t('home.mix.title')}</h2>
          <p className="section-sub">{t('home.mix.subtitle')}</p>
        </Reveal>

        <Reveal delay={0.1} className="mixer-layout">
          <div className={`mixer-stage ${dragging ? 'is-dragging' : ''} ${webgl ? '' : 'no-webgl'}`} onContextMenu={(e) => e.preventDefault()} data-testid="mixer-stage">
            {webgl && (
              <SceneBoundary fallback={<div className="mixer-fallback" />}>
                <Suspense fallback={<div className="mixer-fallback" />}>
                  <KitMixerScene
                    isMobile={isMobile}
                    active={active}
                    products={products}
                    slots={slots}
                    selectedSlot={selectedSlot}
                    kitName=""
                    kitFunction={kitFunction}
                    onHoverProduct={setHoverProduct}
                    onAssign={assign}
                    onSelectSlot={selectSlot}
                    onDragChange={setDragging}
                  />
                </Suspense>
              </SceneBoundary>
            )}
            <AnimatePresence mode="wait">
              {isMobile && showTooltip ? tooltip : (
                <motion.div key={topKey} className="mixer-hint" style={{ x: '-50%' }} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }} data-testid="mixer-hint">
                  {dragging ? <strong>{t('home.mix.hint_drop')}</strong> : (
                    <>
                      <strong data-testid="mixer-step">{step}</strong>
                      <span>{hint}</span>
                    </>
                  )}
                </motion.div>
              )}
            </AnimatePresence>
            {!isMobile && <AnimatePresence>{showTooltip && tooltip}</AnimatePresence>}
          </div>

          <div ref={summaryRef} className="mixer-bottom">
            <AnimatePresence>{recommendOpen && <RecommendPicker key="rec" onPick={recommend} onClose={() => setRecommendOpen(false)} />}</AnimatePresence>
            <AnimatePresence mode="wait" initial={false}>
              {complete ? (
                <KitSummary
                  key="summary"
                  items={items}
                  kitFunction={kitFunction}
                  total={total}
                  onAddToCart={addKit}
                  onReset={reset}
                  onRandom={randomize}
                  onRecommend={() => setRecommendOpen((v) => !v)}
                />
              ) : (
                <motion.div key="bar" className="mixer-bar" initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }} transition={{ duration: 0.25 }} data-testid="mixer-controls">
                  <div className="mix-progress-wrap">
                    <span className="mix-progress" data-testid="mix-progress">{t('home.mix.filled').replace('{n}', filled)}</span>
                    <span className="mix-progress-dots" aria-hidden>
                      {JAR_TYPE_ORDER.map((tp) => <i key={tp} className={slots[tp] ? 'on' : ''} style={slots[tp] ? { background: COLORS[slots[tp].line_color], boxShadow: `0 0 10px ${COLORS[slots[tp].line_color]}` } : undefined} />)}
                    </span>
                    <span className="mix-volume" data-testid="mix-volume">{t('home.mix.volume')}</span>
                  </div>
                  <div className="mix-actions-row">
                    <button onClick={() => setRecommendOpen((v) => !v)} className={`btn-pill btn-pill-ghost ${recommendOpen ? 'is-active' : ''}`} data-testid="mix-recommend">{t('home.mix.recommend')}</button>
                    <button onClick={randomize} className="btn-pill btn-pill-ghost" data-testid="mix-random">{t('home.mix.random')}</button>
                    <button onClick={reset} className="btn-pill btn-pill-ghost" disabled={filled === 0} data-testid="mix-reset">{t('home.mix.reset')}</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default KitMixer;
